/**
 * Bulletin utility functions for React Native admin app
 * Based on admin-web V2 bulletins implementation
 */

import { 
  IBulletin, 
  IBulletinTableRow, 
  ICreateBulletin,
  IBulletinFormValues,
  BulletinFeedFilter 
} from '@/types/admin/bulletins';
import { EMPTY_MESSAGES } from '@/constants/admin';

/**
 * Convert bulletin data to table row format for display
 * Similar to normalizeTableRow in resident list
 */
export const normalizeBulletinTableRow = (bulletin: IBulletin): IBulletinTableRow => {
  // Match admin-web data transformation logic exactly
  return {
    id: bulletin.id,
    // Use title as primary field (from API response), but also set subject for compatibility
    title: bulletin.title || 'Untitled',
    subject: bulletin.title || 'Untitled', // Admin-web uses subject as main field
    // Transform createdAt to both date and time (matching admin-web logic)
    date: bulletin.createdAt ? new Date(bulletin.createdAt) : null,
    time: bulletin.createdAt ? new Date(bulletin.createdAt) : null,
    // Ensure numeric values are properly converted
    messageSentCount: Number(bulletin.messageSentCount) || 0,
    messageReadCount: Number(bulletin.messageReadCount) || 0,
    pushNotificationSentCount: Number(bulletin.pushNotificationSentCount) || 0,
    highlight: Boolean(bulletin.highlight),
    pinHomepage: Boolean(bulletin.pinHomepage),
    publishType: bulletin.publishType,
    // Carry publishDate for Scheduled display
    publishDate: bulletin.publishDate || null,
    recipientType: bulletin.recipientType || 'all',
    updatedAt: bulletin.updatedAt || null, // Preserve updatedAt for unique keys
    // Add fields needed for admin-web v2 compatibility
    description: bulletin.description || '',
    author: bulletin.author || null,
    files: bulletin.files || [],
  } as IBulletinTableRow;
};

/**
 * Convert form values to create bulletin payload
 * Similar to convertFormValuesToBulletin in admin-web
 */
export const convertFormValuesToBulletin = (
  formValues: IBulletinFormValues,
  propertyId: number
): ICreateBulletin => {
  return {
    title: formValues.title,
    message: formValues.message,
    propertyId,
    publishType: formValues.publishType,
    publishDate: formValues.publishDate,
    highlight: formValues.highlight,
    sendEmail: formValues.sendEmail,
    pinHomepage: formValues.pinHomepage,
    recipientType: formValues.recipientType,
    photos: formValues.photos || [],
  };
};

/**
 * Format date for display in bulletin list
 */
export const formatBulletinDate = (date: Date | string | null): string => {
  if (!date) return 'No Details Found';
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  return dateObj.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

/**
 * Format time for display in bulletin list
 */
export const formatBulletinTime = (date: Date | string | null): string => {
  if (!date) return 'No Details Found';
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  return dateObj.toLocaleTimeString('en-US', {
    timeStyle: 'short',
  });
};

/**
 * Get recipient type display label (matching admin-web v2)
 */
export const getRecipientTypeLabel = (recipientType: string): string => {
  switch (recipientType) {
    case 'all':
      return 'All residents';
    case 'current':
      return 'Current residents';
    case 'future':
      return 'Future residents';
    case 'notice':
      return 'Lease notice';
    default:
      return 'All residents';
  }
};

/**
 * Get publish type display label
 */
export const getPublishTypeLabel = (publishType?: string): string => {
  switch (publishType) {
    case 'publishNow':
      return 'Published';
    case 'scheduled':
      return 'Scheduled';
    default:
      return 'Published';
  }
};

/**
 * Check if bulletin is scheduled
 */
export const isBulletinScheduled = (bulletin: IBulletin): boolean => {
  return bulletin.publishType === 'scheduled' && 
         !!bulletin.publishDate && 
         new Date(bulletin.publishDate) > new Date();
};

/**
 * Get bulletin status for display
 * @param bulletin - The bulletin object
 * @param feedFilter - Current feed filter context (Feed/Scheduled/Draft)
 */
export const getBulletinStatus = (bulletin: IBulletin, feedFilter?: string): string => {
  // Use feed filter context to determine status, not bulletin properties
  if (feedFilter === 'Draft') {
    return 'Draft';
  }
  if (feedFilter === 'Scheduled' || isBulletinScheduled(bulletin)) {
    return 'Scheduled';
  }
  return 'Published';
};

/**
 * Calculate read percentage for bulletin
 */
export const calculateReadPercentage = (bulletin: IBulletin): number => {
  if (!bulletin.messageSentCount || bulletin.messageSentCount === 0) {
    return 0;
  }
  return Math.round((bulletin.messageReadCount / bulletin.messageSentCount) * 100);
};

/**
 * Calculate delivery percentage for bulletin
 */
export const calculateDeliveryPercentage = (bulletin: IBulletin): number => {
  if (!bulletin.messageSentCount || bulletin.messageSentCount === 0) {
    return 0;
  }
  return Math.round((bulletin.pushNotificationSentCount / bulletin.messageSentCount) * 100);
};

/**
 * Validate bulletin form data
 */
export const validateBulletinForm = (formValues: IBulletinFormValues): string[] => {
  const errors: string[] = [];

  if (!formValues.title.trim()) {
    errors.push('Title is required');
  }

  if (!formValues.message.trim()) {
    errors.push('Message is required');
  }

  if (formValues.publishType === 'scheduled' && !formValues.publishDate) {
    errors.push('Publish date is required for scheduled bulletins');
  }

  if (formValues.publishType === 'scheduled' && formValues.publishDate) {
    const publishDate = new Date(formValues.publishDate);
    const now = new Date();
    if (publishDate <= now) {
      errors.push('Publish date must be in the future');
    }
  }

  return errors;
};

/**
 * Get bulletin priority indicator
 */
export const getBulletinPriority = (bulletin: IBulletin): 'high' | 'normal' => {
  return bulletin.highlight || bulletin.pinHomepage ? 'high' : 'normal';
};

/**
 * Format bulletin message for preview (truncate if needed)
 */
export const formatBulletinMessagePreview = (message: string, maxLength: number = 150): string => {
  if (!message) return '';
  
  // Strip HTML tags if present
  const plainText = message.replace(/<[^>]*>/g, '');
  
  if (plainText.length <= maxLength) {
    return plainText;
  }
  
  return plainText.substring(0, maxLength).trim() + '...';
};

/**
 * Get the empty-state message object for bulletins by feed filter
 */
export const getBulletinsEmptyMessage = (feedFilter: BulletinFeedFilter) => {
  if (feedFilter === 'Scheduled') return EMPTY_MESSAGES.bulletins.feed.Scheduled;
  if (feedFilter === 'Draft') return EMPTY_MESSAGES.bulletins.feed.Draft;
  return EMPTY_MESSAGES.bulletins.feed.Default;
};
